import { Fragment, type ReactNode } from "react";
import type { PageMetadata, PageSlotData } from "~/app/api/pageData";
import { canRenderCmsComponentType, renderCmsSlotComponent } from "~/app/shell/cmsSlotRendering";
import { SmartEditWrapper } from "~/app/shell/SmartEditWrapper";
import { warnMissingSlot } from "./pageTemplateLogger";

export interface PageSlotLookup {
  renderSlot(position: string): ReactNode;
  getConsumedPositions(): string[];
}

export function createPageSlotLookup(
  slots: PageSlotData[],
  metadata: PageMetadata,
  templateUid: string,
): PageSlotLookup {
  const consumed = new Set<string>();

  const renderSlot = (position: string): ReactNode => {
    const slot = slots.find((candidate) => candidate.position === position);

    if (!slot) {
      warnMissingSlot(templateUid, position);
      return null;
    }

    consumed.add(slot.position);

    if (slot.components.length === 0 && !slot.smartEdit) {
      return null;
    }

    return (
      <SmartEditWrapper kind="slot" smartEdit={slot.smartEdit}>
        {slot.components.map((component) => {
          if (!canRenderCmsComponentType(component.typeCode)) {
            throw new Error(
              `Unsupported CMS component type in page template "${templateUid}", slot "${slot.position}", ` +
                `component uid "${component.uid ?? "<unknown>"}", typeCode "${component.typeCode}".`,
            );
          }

          return (
            <Fragment key={component.uid ?? `${slot.position}-${component.typeCode}`}>
              {renderCmsSlotComponent(component, metadata)}
            </Fragment>
          );
        })}
      </SmartEditWrapper>
    );
  };

  return {
    renderSlot,
    getConsumedPositions: () => Array.from(consumed),
  };
}
